import { useState } from "react";
import { SAUNA_ENQUIRY_OPTIONS } from "@/data/sauna-enquiry-options";
import { MessageSquare, ClipboardCheck, Calculator, Settings, Wrench, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import FileUploader, { type UploadedFile } from "@/components/file-uploader";

type EnquiryType = "General" | "Survey" | "Quote" | "Maintenance" | "Repair" | "Emergency" | null;

export function GetInTouch() {
  const [selectedType, setSelectedType] = useState<EnquiryType>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const { toast } = useToast();

  const options = [
    { id: "General", label: "General Enquiries", icon: <MessageSquare className="w-6 h-6" /> },
    { id: "Survey", label: "Request a Free Survey", icon: <ClipboardCheck className="w-6 h-6" /> },
    { id: "Quote", label: "Request a Free Quote", icon: <Calculator className="w-6 h-6" /> },
    { id: "Maintenance", label: "Request a Maintenance Quote", icon: <Settings className="w-6 h-6" /> },
    { id: "Repair", label: "Request a Repair Visit", icon: <Wrench className="w-6 h-6" /> },
    { id: "Emergency", label: "Emergency Callout", icon: <AlertTriangle className="w-6 h-6" /> }
  ] as const;

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("fullName"),
          email: data.get("email"),
          phone: data.get("phone"),
          message: data.get("message") || `${selectedType} enquiry submitted`,
          formType: `Get In Touch — ${selectedType}`,
          extra: {
            Company: data.get("companyName") || "",
            Address: data.get("address") || "",
            Service: data.get("service") || "",
          },
          attachments: files.map(f => ({ name: f.name, mimeType: f.mimeType, data: f.data })),
        }),
      });
      if (!res.ok) throw new Error("Failed to send");
      toast({
        title: "Enquiry sent",
        description: "We'll be in touch shortly.",
      });
      form.reset();
      setFiles([]);
      setSelectedType(null);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us on 020 8087 0906.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="get-in-touch" className="py-12 lg:py-16 border-t border-border" style={{ backgroundColor: '#e8eaee' }}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Get In Touch</h2>
          <p className="text-lg text-muted-foreground">How can we help? Choose one of the options below:</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedType(opt.id)}
              data-testid={`button-enquiry-${opt.id.toLowerCase()}`}
              className={`group p-6 rounded-2xl border flex flex-col items-center justify-center gap-4 transition-all duration-200
                ${selectedType === opt.id 
                  ? 'border-primary bg-primary text-white shadow-md' 
                  : 'border-border bg-card text-foreground hover:border-primary hover:bg-primary hover:text-white hover:shadow-md'
                }
              `}
            >
              <div className={selectedType === opt.id ? "text-white" : "text-muted-foreground group-hover:text-white"}>
                {opt.icon}
              </div>
              <span className="font-semibold text-center text-sm md:text-base leading-tight">{opt.label}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {selectedType && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-card border border-border rounded-3xl p-8 shadow-sm mt-4">
                <h3 className="text-2xl font-display font-bold mb-6">
                  {options.find(o => o.id === selectedType)?.label}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="enquiry-service">Service</Label>
                    <select id="enquiry-service" name="service" className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      <option value="">Select a service (optional)</option>
                      <option value="Air Conditioning">Air Conditioning</option>
                      <option value="Ventilation">Ventilation</option>
                      <option value="Heating">Heating</option>
                      <option value="Ductwork">Ductwork</option>
                      <option value="BMS & Controls">BMS & Controls</option>
                      {SAUNA_ENQUIRY_OPTIONS.map(service => <option key={service} value={service}>{service}</option>)}
                    </select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input id="fullName" name="fullName" required placeholder="John Smith" data-testid="input-contact-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name (Optional)</Label>
                      <Input id="companyName" name="companyName" placeholder="Acme Ltd" data-testid="input-contact-company" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Address / Postcode</Label>
                    <Input id="address" name="address" required placeholder="London, SW1A 1AA" data-testid="input-contact-address" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" name="phone" required type="tel" placeholder="07xxx xxxxxx" data-testid="input-contact-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" name="email" required type="email" placeholder="john@example.com" data-testid="input-contact-email" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" name="message" required className="min-h-[120px]" placeholder="How can we assist you today?" data-testid="input-contact-message" />
                  </div>

                  <FileUploader files={files} onChange={setFiles} label="Attach photos, PDFs or Excel (optional)" />

                  <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => setSelectedType(null)} data-testid="button-contact-cancel">
                      Cancel
                    </Button>
                    <Button type="submit" size="lg" disabled={submitting} data-testid="button-contact-submit">
                      {submitting ? "Sending..." : "Submit Enquiry"}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
